
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import React, { useRef, useState, useEffect } from "react";
import {GoogleMap, LoadScript, DirectionsRenderer} from "@react-google-maps/api";
import Papa from "papaparse";

const BACKEND_URL = process.env.REACT_APP_BASE_URL;
const WS_URL = process.env.REACT_APP_WS_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function AdminDashboard() {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // 'YYYY-MM-DD'
  });

  const [selectedTime, setSelectedTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // 'HH:MM'
  });

  const [start, setStart] = useState("");
  const [stops, setStops] = useState([""]);
  const [result, setResult] = useState(null);
  const [directions, setDirections] = useState(null);
  const [alerts, setAlerts] = useState([]);  // Combined historic + live
  const [Transporter, setTransporter] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [leftWidth, setLeftWidth] = useState(35);
  const [loading, setLoading] = useState(false);

  // Notification Panel States
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const isDragging = useRef(false);
  const mapRef = useRef(null);

  const MAX_ALERTS = 10;  // max number of alerts to show

  const addAlert = (msg) => {
    setAlerts((prev) => {
      const updated = [...prev, msg];
      // keep only the last MAX_ALERTS alerts
      if (updated.length > MAX_ALERTS) {
        return updated.slice(updated.length - MAX_ALERTS);
      }
      return updated;
    });
  };

  const normalizeStops = (inputStops) => {
    return Array.from(
      new Set(
        inputStops
          .map((s) => s.trim())
          .filter((s) => s && isNaN(Number(s)))
      )
    );
  };

  const markAsRead = (id) => {
    setAllNotifications(prev => {
      const updated = prev.map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      setUnreadCount(updated.filter(n => !n.read).length);
      return updated;
    });

    const token = sessionStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/routes/admin/notifications/read/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    }).catch(err => console.error("Failed to mark read:", err));
  };
  const appendStops = (loadedStops) => {
    const combined = [...stops, ...loadedStops];
    setStops(normalizeStops(combined));
  };

  const handleAddStop = () => setStops([...stops, ""]);

  const handleChangeStop = (index, value) => {
    const newStops = [...stops];
    newStops[index] = value;
    setStops(newStops);
  };

  const handleDeleteStop = (index) => {
    if (stops.length === 1) return;
    setStops(stops.filter((_, i) => i !== index));
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    const handleLines = (lines) => {
      const validLines = lines
        .map((line) => line.trim())
        .filter((line) => line && isNaN(Number(line)));

      if (validLines.length === 0) {
        alert("The file contains no valid stop locations.");
        return;
      }

      appendStops(validLines);
    };

    if (file.name.endsWith(".csv")) {
      Papa.parse(file, {
        complete: (results) => {
          if (!Array.isArray(results.data) || results.data.length === 0) {
            alert("Empty or malformed CSV file.");
            return;
          }
          const lines = results.data.flat().filter(Boolean);
          handleLines(lines);
        },
        error: (err) => {
          console.error("CSV parsing error:", err);
          alert("Failed to parse CSV file.");
        },
      });
    } else {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text === "string") {
          const lines = text.split(/\r?\n/).filter(Boolean);
          handleLines(lines);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const newLeftWidth = (e.clientX / window.innerWidth) * 100;
    if (newLeftWidth > 20 && newLeftWidth < 80) {
      setLeftWidth(newLeftWidth);
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (directions && mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      if (directions.routes && directions.routes.length > 0) {
        directions.routes[0].legs.forEach((leg) => {
          bounds.extend(leg.start_location);
          bounds.extend(leg.end_location);
        });
        mapRef.current.fitBounds(bounds);
      }
    }
  };

  const pushNotification = (msg) => {
    setAllNotifications(prev => {
      const newList = [
        { id: Date.now(), message: msg, timestamp: new Date().toISOString(), read: false },
        ...prev
      ];

      setUnreadCount(newList.filter(n => !n.read).length);

      return newList;
    });
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [directions]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${BACKEND_URL}/api/routes/admin/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        const mapped = data.map(n => ({
          id: n.id,
          message: n.message,
          timestamp: n.created_at,
          read: n.read
        }));

        setAllNotifications(mapped);
        setUnreadCount(mapped.filter(n => !n.read).length);
      })
      .catch(err => console.error("Notification load failed:", err));

  }, []);

  useEffect(() => {
    if (!isNotifOpen) return;

    const token = sessionStorage.getItem("token");

    // Mark all notifications as read in the frontend
    setAllNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      setUnreadCount(0);
      return updated;
    });

    // Optionally, inform backend that notifications are read
    fetch(`${BACKEND_URL}/api/routes/admin/notifications/mark-read`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    }).catch(err => console.error("Failed to mark notifications as read:", err));
  }, [isNotifOpen]);


  // Fetch Transporters on mount
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    fetch(`${BACKEND_URL}/api/routes/users?role=Transporter`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch Transporter");
        return res.json();
      })
      .then((data) => setTransporter(data))
      .catch((err) => console.error("Error fetching Transporter:", err));
  }, []);

  const handleSubmit = async () => {
    const trimmedStart = start.trim();
    const normalizedStops = normalizeStops(stops);

    if (!trimmedStart || normalizedStops.length === 0) {
      alert("Please fill in the start location and at least one valid stop location.");
      return;
    }
    if (!selectedDate || !selectedTime) {
      alert("Please select departure date and time.");
      return;
    }
    if (!selectedTransporter) {
      alert("Please select a Transporter.");
      return;
    }

    const token = sessionStorage.getItem("token");
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/admin/request_transporter1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          start: trimmedStart,
          stops: normalizedStops,
          departure_date: selectedDate,
          departure_time: selectedTime,
          transporter_id: selectedTransporter,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        alert(data.error || "Failed to optimize route.");
        setResult(null);
        setDirections(null);
        return;
      }

      toast.success("Route successfully optimized and the request has be raised for the transporter!");
      // addAlert("Route successfully optimized and the request has be raised for the transporter!");

      setResult(data);

      if (data.optimized?.ordered_locations?.length >= 2) {
        const origin = data.optimized.ordered_locations[0];
        const destination =
          data.optimized.ordered_locations[data.optimized.ordered_locations.length - 1];
        const waypoints = data.optimized.ordered_locations
          .slice(1, -1)
          .map((stop) => ({ location: stop, stopover: true }));

        if (!window.google || !window.google.maps) {
          alert("Google Maps failed to load.");
          return;
        }

        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
          },
          (resultObj, status) => {
            if (status === "OK") {
              setDirections(resultObj);
            } else {
              console.error("Error fetching directions", resultObj);
              setDirections(null);
            }
          }
        );
      } else {
        setDirections(null);
      }
    } catch (error) {
      console.error("Error submitting route optimization:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // useEffect for fetching past alerts + websocket + ping
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    // 1. Fetch past alerts from backend
    fetch(`${BACKEND_URL}/api/routes/admin/alerts`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch past alerts.");
        }
        return res.json();
      })
      .then((data) => {
        // data: array of alert objects { id, driver_id, route_id, message, created_at, ... }
        const pastAlerts = data.map((a) => a.message);
        
        setAlerts((prev) => {
          const combined = [...pastAlerts, ...prev];
          // keep only last MAX_ALERTS items
          if (combined.length > MAX_ALERTS) {
            return combined.slice(combined.length - MAX_ALERTS);
          }
          return combined;
        });
      })
      .catch((err) => {
        console.error("Error fetching past alerts:", err);
      });

    // 2. Setup WebSocket + live alerts
    const ws = new WebSocket(`${WS_URL}/api/ws/alerts?token=${token}`);

    let pingInterval = null;

    ws.onopen = () => {
      console.log("WebSocket (admin alerts) connected");
      // Setup ping / keep-alive
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          // Send a simple ping text or JSON ping
          try {
            ws.send("ping");
          } catch (e) {
            console.error("Failed to send ping", e);
          }
        }
      }, 30000); // every 30 seconds
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const { event: evt } = data;

        // 🚨 ROUTE DEVIATION
        if (evt === "ROUTE_DEVIATION") {
          const { driver_id, driver_name, location, timestamp, route_id } = data;

          const timeStr = timestamp ? new Date(timestamp).toLocaleString() : "Unknown";
          let locationStr = "Unknown location";

          if (location && location.lat != null && location.lng != null) {
            locationStr = `(${location.lat.toFixed(4)}, ${location.lng.toFixed(4)})`;
          }

          const msg = `⚠️ Driver ${driver_name || driver_id} deviated from route ${route_id} at ${locationStr} on ${timeStr}`;

          toast.warn(msg);
          pushNotification(msg);
          addAlert(msg);
          return;
        }

        // 🚀 START TRIP

        if (evt === "START_TRIP") {
          const msg = `🚀 Driver ${data.driver_name} started route ${data.route_id}`;
          toast.info(msg);
          pushNotification(msg);
          addAlert(msg);
          return;
        }

        // 📍 STOP REACHED

        if (evt === "STOP_REACHED") {
          const msg = `📍 Driver ${data.driver_name} reached stop #${data.stop_order || "?"} — ${data.address || "Unknown address"}`;
          toast.info(msg);
          pushNotification(msg);
          addAlert(msg);
          return;
        }

        // ✅ END TRIP

        if (evt === "END_TRIP") {
          const msg = `✅ Driver ${data.driver_name} completed route ${data.route_id}`;
          toast.success(msg);
          pushNotification(msg);
          addAlert(msg);
          return;
        }

        // ⛔ STOP TRIP (manual stop)
        
        if (evt === "STOP_TRIP") {
          const { driver_name, route_id, timestamp, location } = data;

          const timeStr = timestamp ? new Date(timestamp).toLocaleString() : "Unknown";
          let locationStr = "Unknown location";

          if (location && location.lat != null && location.lng != null) {
            locationStr = `(${location.lat.toFixed(4)}, ${location.lng.toFixed(4)})`;
          }

          const msg = `⛔ Driver ${driver_name} manually stopped route ${route_id} at ${locationStr} on ${timeStr}`;
          toast.error(msg);
          pushNotification(msg);
          addAlert(msg);
          return;
        }

      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };


    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    ws.onclose = (e) => {
      console.log("WebSocket closed:", e.reason || "no reason given");
      // optional: you could attempt reconnect after a short delay
    };

    // Cleanup when component unmounts
    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
      }
      try {
        ws.close();
      } catch (e) {
        console.error("Error closing WebSocket:", e);
      }
    };
  }, []);  // Empty dependency => run once when component mounts

  return (
    <div className="min-h-screen w-screen flex font-sans bg-[#1f1f1f] text-white">

      {/* Left Panel */}
      <div
        className="flex flex-col gap-6 p-6 overflow-y-auto"
        style={{ width: `${leftWidth}%`, height: "100vh" }}
      >
        {/* Top Section */}
        <div className="bg-black p-6 rounded-xl shadow-lg flex flex-col">
          <div className="flex items-center justify-between mb-6 relative z-40">
            <h1 className="text-2xl font-bold text-orange-500">
              Admin Dashboard
            </h1>

            {/* Notification Bell */}
            <div className="relative mr-4">
              <button
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                className="text-orange-400 hover:text-orange-500 text-2xl"
              >
                🔔
              </button>

              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-1 bg-red-600 px-2 py-0.5 text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>

            <button
              onClick={() => {
                sessionStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-orange-400 hover:text-orange-500 transition-colors"
              title="Logout"
            >
              Logout
            </button>
          </div>

          <label className="block text-lg font-semibold text-orange-400 mb-2">
            Start Location
          </label>
          <input
            placeholder="Enter start location"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full px-4 py-1.5 bg-[#2a2a2a] border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          />

          {/* Stops Section */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-orange-500 mb-4">
              Stop Locations
            </h2>
            {stops.map((s, i) => (
              <div key={i} className="mb-3 flex items-center gap-2">
                <input
                  placeholder="Enter stop location"
                  value={s}
                  onChange={(e) => handleChangeStop(i, e.target.value)}
                  className="flex-1 px-4 py-1.5 bg-[#2a2a2a] border border-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {stops.length > 1 && (
                  <button
                    onClick={() => handleDeleteStop(i)}
                    className="text-red-400 hover:text-red-600"
                    title="Delete Stop"
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={handleAddStop}
              className="text-sm text-orange-400 hover:underline mb-4 self-start"
            >
              + Add Stop
            </button>
          </div>
          <div>

            {/* File Upload */}
            <label className="text-sm text-gray-400 mb-1">
              Upload stops from .csv or .txt:
            </label>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={(e) => handleFileUpload(e.target.files?.[0])}
              className="mt-2 bg-[#2a2a2a] border border-gray-700 p-1 rounded text-white"
            />
          </div>

          {/* Departure Date/Time + Driver */}
          <div className="mt-6">
            <label className="block text-sm text-gray-400 mb-1">
              Departure Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-1.5 bg-[#2a2a2a] border border-gray-700 rounded text-white mb-3"
            />

            <label className="block text-sm text-gray-400 mb-1">
              Departure Time
            </label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full px-3 py-1.5 bg-[#2a2a2a] border border-gray-700 rounded text-white mb-3"
            />

            <label className="block text-sm text-gray-400 mb-1">
              Assign Transporter
            </label>
            <select
              value={selectedTransporter}
              onChange={(e) => setSelectedTransporter(parseInt(e.target.value))}
              className="w-full px-3 py-1.5 bg-[#2a2a2a] border border-gray-700 rounded text-white"
            >
              <option value="">-- Select Transporter --</option>
              {Transporter.map((Transporter) => (
                <option key={Transporter.id} value={Transporter.id}>
                  {Transporter.username}
                </option>
              ))}
            </select>
          </div>

          {/* Optimize Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`mt-6 w-full py-2 rounded transition ${loading
              ? "bg-orange-300 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
              }`}
          >
            {loading ? "Optimizing..." : "Optimize & Assign Route"}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div className="bg-black p-6 rounded-xl shadow-lg">
            <h2 className="text-lg font-semibold text-orange-500 mb-2">
              Optimized Route
            </h2>
            <ol className="list-decimal list-inside text-sm text-gray-300 space-y-1">
              {result.optimized?.ordered_locations?.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ol>
            <p className="text-gray-400 mt-2">
              Distance:{" "}
              <span className="text-orange-400">
                {result.optimized?.distance_m
                  ? (result.optimized.distance_m / 1000).toFixed(2)
                  : "--"}{" "}
                km
              </span>
            </p>
            <p className="text-gray-400">
              Estimated Time:{" "}
              <span className="text-orange-400">
                {result.optimized?.duration_s
                  ? (result.optimized.duration_s / 60).toFixed(2)
                  : "--"}{" "}
                mins
              </span>
            </p>
          </div>
        )}

      </div>

      {/* Drag Handle */}
      <div
        onMouseDown={handleMouseDown}
        className="cursor-col-resize w-1 bg-gray-800 hover:bg-gray-600"
      />

      {/* Map Panel */}
      <div style={{ width: `${100 - leftWidth}%`, height: "100vh" }}>
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: 22.5937, lng: 81.9629 }}
            zoom={5}
            mapTypeId="roadmap"
            onLoad={(map) => (mapRef.current = map)}
          >
            {directions && <DirectionsRenderer directions={directions} />}
          </GoogleMap>
        </LoadScript>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="dark"
      />

      {/* Notification Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-[#1b1b1b] border-l border-gray-700 shadow-xl transform transition-transform duration-300 z-50 
              ${isNotifOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-700">
          <h2 className="text-xl font-semibold text-orange-500">Notifications</h2>
          <button onClick={() => setIsNotifOpen(false)} className="text-gray-400 text-xl">✖</button>
        </div>

        <div className="overflow-y-auto h-[calc(100%-60px)] p-3 space-y-3">
          {allNotifications.length === 0 && (
            <p className="text-gray-400 text-center mt-4">No notifications yet</p>
          )}

          {allNotifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => markAsRead(notif.id)}
              className={`p-3 rounded-lg cursor-pointer border 
        ${notif.read ? "bg-[#2a2a2a] border-gray-600" : "bg-[#332211] border-orange-500 shadow-md"}`}
            >
              <p className="text-sm">{notif.message}</p>
              <p className="text-xs text-gray-400 mt-1">
                {new Date(notif.timestamp).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

export default AdminDashboard;
